import { ApolloClient, InMemoryCache, createHttpLink, from } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { fetchAuthSession } from "aws-amplify/auth";
import { getSelectedCustomerId } from "../../hooks/useCustomer";
import { AuthenticationService } from "../authentication";
import { getViewAsToken } from "../impersonation";

// PERF: Cache JWT decoded values to avoid decoding on every request
// Expected improvement: 50-100ms per request
interface CachedTokenInfo {
  tokenHash: string; // Simple hash to detect token changes
  userRole: string | null;
}

let cachedTokenInfo: CachedTokenInfo | null = null;

// Simple hash function for token change detection
const hashToken = (token: string): string => {
  // Use first 20 + last 20 chars as a simple fingerprint (faster than full hash)
  return token.length > 40 ? `${token.slice(0, 20)}...${token.slice(-20)}` : token;
};

// Get cached user role, only decode if token changed
const getCachedUserRole = (idToken: string): string | null => {
  const tokenHash = hashToken(idToken);

  // Return cached value if token hasn't changed
  if (cachedTokenInfo && cachedTokenInfo.tokenHash === tokenHash) {
    return cachedTokenInfo.userRole;
  }

  // Decode and cache
  try {
    const base64 = idToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    const userRole = payload["custom:role"] || null;

    cachedTokenInfo = {
      tokenHash,
      userRole,
    };

    return userRole;
  } catch (error) {
    console.warn("[Apollo Client] Failed to decode token for role check:", error);
    return null;
  }
};

// Create HTTP link to the backend gateway
const getGraphQLUri = () => {
  if (import.meta.env.VITE_BACKENDGATEWAY_URL) {
    return `${import.meta.env.VITE_BACKENDGATEWAY_URL}/graphql`;
  }

  const host = import.meta.env.VITE_HTTP_API_V2_HOST;
  if (!host) {
    throw new Error("Either VITE_BACKENDGATEWAY_URL or VITE_HTTP_API_V2_HOST must be set");
  }

  // VITE_HTTP_API_V2_HOST is always just a hostname without protocol, so prepend https://
  return `https://${host}/backendgateway/graphql`;
};

const httpLink = createHttpLink({
  uri: getGraphQLUri(),
});

// Auth link to add JWT token to requests
// For API Gateway HTTP V2 JWT authorizer: use access token in Authorization header
// Backend gateway requires ID token in x-atomic-idtoken header for user identification
const authLink = setContext(async (_, { headers }) => {
  // Try to get tokens from AuthenticationService first (faster, cached)
  let accessToken = AuthenticationService.getAccessToken();
  let idToken = AuthenticationService.getIdToken();

  // If tokens are not available in AuthenticationService, fetch directly from Amplify
  // This handles cases where requests are made before tokens are set in AuthenticationService
  if (!accessToken || !idToken) {
    try {
      const session = await fetchAuthSession();
      if (session.tokens?.accessToken) {
        const tokenString = session.tokens.accessToken.toString();
        accessToken = tokenString;
        // Update AuthenticationService for future requests
        if (!AuthenticationService.getAccessToken()) {
          AuthenticationService.setAccessToken(tokenString);
        }
      }
      if (session.tokens?.idToken) {
        const tokenString = session.tokens.idToken.toString();
        idToken = tokenString;
        // Update AuthenticationService for future requests
        if (!AuthenticationService.getIdToken()) {
          AuthenticationService.setIdToken(tokenString);
        }
      }
    } catch (error) {
      console.error("Failed to fetch auth session:", error);
    }
  }

  if (!accessToken) {
    console.warn("No access token available for API Gateway authentication");
    return { headers };
  }

  if (!idToken) {
    console.error("No ID token available - required for backend gateway user identification", {
      hasAccessToken: !!accessToken,
      authServiceHasIdToken: !!AuthenticationService.getIdToken(),
    });
    // Still return headers to allow the request to proceed, but backend will reject it
    // This allows for better error handling on the backend
  }

  // Extract token from Bearer if already present, otherwise use raw token
  const accessTokenValue = accessToken.startsWith("Bearer ")
    ? accessToken.substring(7)
    : accessToken;

  const headersToReturn: Record<string, string> = {
    ...headers,
    // Ensure Content-Type is set for GraphQL requests (required for Apollo Server CSRF protection)
    "Content-Type": "application/json",
    // API Gateway HTTP V2 expects access token in Authorization header
    authorization: `Bearer ${accessTokenValue}`,
  };

  // Always send ID token if available - backend requires it for user identification
  // The ID token contains user info (email, role, customerId) that the backend needs
  if (idToken) {
    const idTokenValue = idToken.startsWith("Bearer ") ? idToken.substring(7) : idToken;
    headersToReturn["x-atomic-idtoken"] = idTokenValue;

    // PERF: Check if user is ROOT using cached decoded value
    const userRole = getCachedUserRole(idTokenValue);
    if (userRole === "ROOT") {
      const selectedCustomerId = getSelectedCustomerId();
      headersToReturn["x-atomic-customerid"] = selectedCustomerId;
    }
  } else {
    console.warn(
      "[Apollo Client] ID token not available - request will likely fail authentication",
      {
        hasAccessToken: !!accessToken,
        triedAmplifyFetch: true,
      }
    );
  }

  // View As (impersonation): when a session is active, ride its token alongside
  // the real tokens. The backend honors it only when the real caller is ROOT, and
  // it only ever narrows privilege.
  const viewAsToken = getViewAsToken();
  if (viewAsToken) {
    headersToReturn["x-impersonation"] = viewAsToken;
  }

  return { headers: headersToReturn };
});

// Create Apollo Client with authentication
export const apolloClient = new ApolloClient({
  link: from([authLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: {
      // Add any custom cache policies here if needed
    },
  }),
  defaultOptions: {
    watchQuery: {
      errorPolicy: "all",
    },
    query: {
      errorPolicy: "all",
    },
  },
});

// Helper function to get Apollo Client instance
export const getApolloClient = () => apolloClient;
